import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import Loading from '../../components/Loading/Loading';

const OUTCOME_BADGE = {
  INSERTED: 'bg-green-lt',
  DUPLICATE_SKIPPED: 'bg-blue-lt',
  UNMATCHED_SKIPPED: 'bg-red-lt',
  AMBIGUOUS_SKIPPED: 'bg-orange-lt',
  NO_PROJECT_SKIPPED: 'bg-yellow-lt',
};

const AuditQTransactionImportReview = () => {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setAlert(null);
    try {
      const [summaryRes, rowsRes] = await Promise.all([
        api.get('/audit/auditq-transaction-import/summary'),
        api.get('/audit/auditq-transaction-import/rows', {
          params: outcomeFilter ? { outcome: outcomeFilter, limit: 200 } : { limit: 200 },
        }),
      ]);
      setSummary(summaryRes.data);
      setRows(Array.isArray(rowsRes.data) ? rowsRes.data : []);
    } catch (error) {
      setAlert({
        type: 'danger',
        message: error?.response?.data?.message || 'Failed to load AuditQ transaction import review data.',
      });
    } finally {
      setLoading(false);
    }
  }, [outcomeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const statCards = summary ? [
    { label: 'Total Staged', value: summary.total, filter: '' },
    { label: 'Inserted', value: summary.inserted, filter: 'INSERTED' },
    { label: 'Duplicate (blocked)', value: summary.duplicate_skipped, filter: 'DUPLICATE_SKIPPED' },
    { label: 'Unmatched company', value: summary.unmatched_skipped, filter: 'UNMATCHED_SKIPPED' },
    { label: 'Ambiguous match', value: summary.ambiguous_skipped, filter: 'AMBIGUOUS_SKIPPED' },
    { label: 'No project yet', value: summary.no_project_skipped, filter: 'NO_PROJECT_SKIPPED' },
  ] : [];

  return (
    <div className="container-xl py-4">
      <div className="page-header d-print-none mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h2 className="page-title mb-1">AuditQ Transaction Import Review</h2>
            <div className="text-muted">
              Partial import from AuditQ AuditTransaction JSON, matched against existing OneAlpha companies/projects.
              Staged for review in <code>audit_transaction_import</code> — nothing here has been written into
              live Audit Platform records (project_service_standards) yet.
            </div>
          </div>
          <div className="col-auto">
            <button type="button" className="btn btn-outline-primary" onClick={fetchData} disabled={loading}>
              <RefreshCcw size={16} className="me-1" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {loading ? <Loading /> : (
        <>
          <div className="row g-3 mb-3">
            {statCards.map((card) => (
              <div className="col-sm-6 col-xl-2" key={card.label}>
                <button
                  type="button"
                  className={`border rounded p-3 bg-white h-100 w-100 text-start ${outcomeFilter === card.filter ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setOutcomeFilter(card.filter)}
                >
                  <div className="text-muted small">{card.label}</div>
                  <div className="h2 mb-0">{card.value}</div>
                </button>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title mb-0">Staged Rows{outcomeFilter ? ` — ${outcomeFilter}` : ''}</h3>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Audit Code</th>
                    <th>Client (AuditQ)</th>
                    <th>Matched Company</th>
                    <th>Matched Project</th>
                    <th>Standard</th>
                    <th>Cycle</th>
                    <th>Contract</th>
                    <th>Match</th>
                    <th>Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={9} className="text-muted text-center">No rows</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.id}>
                      <td className="text-mono">{r.audit_code}<span className="text-muted">#{r.segment_index}</span></td>
                      <td>{r.client_name}</td>
                      <td>{r.matched_company_name || <span className="text-muted">—</span>}</td>
                      <td>{r.matched_project_code || <span className="text-muted">—</span>}</td>
                      <td>{r.standard_name}</td>
                      <td>{r.audit_type}</td>
                      <td className="text-muted">{r.contract_number}<br />{r.contract_date}</td>
                      <td>
                        <span className="badge bg-secondary-lt">{r.match_status}</span>
                        {r.match_confidence && <div className="text-muted small">{r.match_confidence}</div>}
                      </td>
                      <td><span className={`badge ${OUTCOME_BADGE[r.outcome] || 'bg-secondary-lt'}`}>{r.outcome}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AuditQTransactionImportReview;
